import React from 'react';
import { layout, palette } from '@citrite/web-ui-component';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { CitrixBranding } from 'App/CitrixBranding';
import { ServerName } from 'App/ServerName';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';

const StyledFooterDivBase = styled.div<{
	bottomOffset: number;
}>`
	display: flex;
	flex-direction: column;
	position: fixed;
	width: 100%;
	justify-content: space-between;
	align-items: center;
	left: 50%;
	transform: translateX(-50%);
	box-sizing: border-box;
`;
const StyledFooterDivLegacy = styled(StyledFooterDivBase)`
	max-width: 1072px;
	bottom: ${layout.mediumSpace};
	@media (max-width: 1200px) {
		padding-left: 64px;
		padding-right: 64px;
	}
	${props =>
		props.bottomOffset &&
		`
		bottom: ${props.bottomOffset}px;
	`}
`;

const StyledFooterDivDetailed = styled(StyledFooterDivBase)`
	max-width: 1064px;
	@media (max-width: 1104px) {
		max-width: min(1064px, calc(100vw - 40px));
	}

	${props =>
		props.bottomOffset &&
		`
		bottom: ${props.bottomOffset}px;
	`}
`;

/**	CSS explanation:
 * max width of the Footer section is 1072px (same as the Tabs section)
 */

const StyledHorizontalLineLegacy = styled.hr`
	margin: ${layout.smallSpace} 0;
	border-top: 1px solid ${palette.grey600};
	border-bottom: 0px;
	border-left: 0px;
	width: 100%;
`;

const StyledHorizontalLineDetailed = styled(StyledHorizontalLineLegacy)`
	border-top: 1px solid ${palette.grey300};
	margin: 0px;
`;

const StyledInnerContainerLegacy = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	margin: 0;
`;

const StyledInnerContainerDetailed = styled(StyledInnerContainerLegacy)`
	padding: ${layout.smallSpace} 0;
	box-sizing: border-box;
`;

const elements: JSX.Element[] = [
	<CitrixBranding key="citrix-branding" />,
	<ServerName key="server-name" />,
];

export const AppFooter = () => {
	const theme = useTheme();
	const bottomOffset = theme?.mastheadOffset?.daasBottomOffset;

	const isDetailedClassicViewEnabled = useFeatureCanary(
		FeatureFlag.DetailedDesktopClassicView
	);

	const StyledFooterDiv = isDetailedClassicViewEnabled
		? StyledFooterDivDetailed
		: StyledFooterDivLegacy;

	const StyledHorizontalLine = isDetailedClassicViewEnabled
		? StyledHorizontalLineDetailed
		: StyledHorizontalLineLegacy;

	const StyledInnerContainer = isDetailedClassicViewEnabled
		? StyledInnerContainerDetailed
		: StyledInnerContainerLegacy;

	return (
		<StyledFooterDiv bottomOffset={bottomOffset} data-testid="app-footer-container">
			<StyledHorizontalLine />
			<StyledInnerContainer>
				{elements.map(element => (
					<React.Fragment key={element.key}>{element}</React.Fragment>
				))}
			</StyledInnerContainer>
		</StyledFooterDiv>
	);
};
